import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { moduloApi } from '../api/moduloApi';

export const useModulos = () => {
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Obtener todos los módulos
  const fetchModulos = useCallback(async (zonaId = null, estadoId = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await moduloApi.getModulos(zonaId, estadoId);
      setModulos(response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener módulos';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener módulos con estadísticas
  const fetchModulosWithStats = useCallback(async (zonaId = null, estadoId = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await moduloApi.getModulosWithStats(zonaId, estadoId);
      setModulos(response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener módulos con estadísticas';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener módulo por ID
  const fetchModuloById = useCallback(async (moduloId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await moduloApi.getModuloById(moduloId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener módulo';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener módulos por zona
  const fetchModulosByZona = useCallback(async (zonaId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await moduloApi.getModulosByZona(zonaId);
      setModulos(response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener módulos de la zona';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Crear módulo
  const createModulo = useCallback(async (moduloData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await moduloApi.createModulo(moduloData);
      setModulos(prev => [...prev, response.data]);
      toast.success('Módulo creado exitosamente');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al crear módulo';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Actualizar módulo
  const updateModulo = useCallback(async (moduloId, moduloData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await moduloApi.updateModulo(moduloId, moduloData);
      setModulos(prev => 
        prev.map(modulo => 
          modulo.id === moduloId ? response.data : modulo
        )
      );
      toast.success('Módulo actualizado exitosamente');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al actualizar módulo';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Eliminar módulo
  const deleteModulo = useCallback(async (moduloId) => {
    setLoading(true);
    setError(null);
    
    try {
      await moduloApi.deleteModulo(moduloId);
      setModulos(prev => prev.filter(modulo => modulo.id !== moduloId));
      toast.success('Módulo eliminado exitosamente');
      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al eliminar módulo';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Buscar módulo por código
  const searchModuloByCodigo = useCallback(async (codigo) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await moduloApi.getModuloByCodigo(codigo);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al buscar módulo';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener estadísticas de un módulo
  const fetchModuloStatistics = useCallback(async (moduloId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await moduloApi.getModuloStatistics(moduloId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener estadísticas del módulo';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Limpiar error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Estado
    modulos,
    loading,
    error,
    
    // Acciones
    fetchModulos,
    fetchModulosWithStats,
    fetchModuloById,
    fetchModulosByZona,
    createModulo,
    updateModulo,
    deleteModulo,
    searchModuloByCodigo,
    fetchModuloStatistics,
    clearError,
  };
};
